import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '../api/api';

const TYPE_LABELS = {
  cabinet: 'Cabinet comptable',
  cabinet_medical: 'Cabinet médical',
  company: 'Entreprise',
};

const ACTIVITY_LABELS = {
  service: 'Activité de service',
  commercial: 'Activité commerciale',
  industry: 'Activité industrielle',
};

export default function TenantSignupModal({ onClose }) {
  const [form, setForm] = useState({
    cabinetName: '',
    type: 'cabinet',
    country: 'TN',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    activity: 'service',
    gestionCabinet: false,
  });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const payload = {
        cabinetName: form.cabinetName,
        type: form.type,
        country: form.country,
        adminName: form.adminName,
        adminEmail: form.adminEmail,
        adminPassword: form.adminPassword,
        isTrial: true,
      };

      if (form.type === 'company') {
        payload.activity = form.activity;
      }

      if (form.type === 'cabinet') {
        payload.gestionCabinet = form.gestionCabinet;
      }

      await api.post('/public/tenants/register', payload);

      setSuccessMsg(
        "Compte crée avec succès ! Votre essai gratuit est actif. Connectez-vous maintenant."
      );
    } catch (err) {
      console.error('Erreur inscription:', err);
      const msg = err.response?.data?.message || "Erreur lors de l'inscription.";
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputClassName = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all";

  const typeLabel = TYPE_LABELS[form.type] || 'Compte';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Démarrer l'essai gratuit</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {successMsg ? (
          <div className="bg-green-50 border border-green-200 text-green-800 text-sm p-4 rounded-lg text-center">
            <p className="font-semibold mb-2">{successMsg}</p>
            <button onClick={onClose} className="mt-3 text-indigo-600 font-bold hover:underline">
              Fermer et se connecter
            </button>
          </div>
        ) : (
          <>
            {errorMsg && (
              <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-3 rounded mb-4">
                {errorMsg}
              </div>
            )}
            <form onSubmit={submit} className="space-y-4">
              {/* Type de compte */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Type de compte</label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className={inputClassName}
                >
                  {Object.entries(TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  {form.type === 'company' ? "Nom de l'entreprise" : 'Nom du cabinet'}
                </label>
                <input
                  name="cabinetName"
                  value={form.cabinetName}
                  onChange={handleChange}
                  required
                  className={inputClassName}
                  placeholder={form.type === 'company' ? 'Ex: Entreprise Beta' : 'Ex: Cabinet Alpha'}
                />
              </div>

              {/* Activity selector for companies */}
              {form.type === 'company' && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">Activité principale</label>
                  <select
                    name="activity"
                    value={form.activity}
                    onChange={handleChange}
                    className={inputClassName}
                  >
                    {Object.entries(ACTIVITY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Gestion cabinet module for cabinet comptable */}
              {form.type === 'cabinet' && (
                <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="gestionCabinet"
                    name="gestionCabinet"
                    checked={form.gestionCabinet}
                    onChange={handleChange}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="gestionCabinet" className="text-sm font-medium text-gray-800 cursor-pointer">
                    Ajouter le module Gestion de Cabinet (Tâches + Récurrentes)
                  </label>
                </div>
              )}

              {/* Info text for medical cabinet */}
              {form.type === 'cabinet_medical' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  Signature électronique activée par défaut. Les factures seront émises en tant que <strong>Notes d'honoraires</strong>.
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Pays</label>
                <select
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  className={inputClassName}
                >
                  <option value="TN">Tunisie</option>
                  <option value="FR">France</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">Responsable</label>
                  <input
                    name="adminName"
                    value={form.adminName}
                    onChange={handleChange}
                    required
                    className={inputClassName}
                    placeholder="Nom complet"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">Email (Login)</label>
                  <input
                    type="email"
                    name="adminEmail"
                    value={form.adminEmail}
                    onChange={handleChange}
                    required
                    className={inputClassName}
                    placeholder="email@exemple.tn"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Mot de passe</label>
                <input
                  type="password"
                  name="adminPassword"
                  value={form.adminPassword}
                  onChange={handleChange}
                  required
                  className={inputClassName}
                  placeholder="••••••••"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  Annuler
                </button>
                <button type="submit" disabled={loading} className="px-6 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-md transition-all disabled:opacity-70">
                  {loading ? 'Activation...' : "Activer l'essai (14j)"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

TenantSignupModal.propTypes = { onClose: PropTypes.func.isRequired };
